// Phase 217 — Bill Vendor ledger page with 3 tabs (Invoices / Payments
// / Statement). Mirrors the apartment ledger UX. The Bills index page
// now only shows summary rows; full transaction history opens here.
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, PencilSimple, Phone, EnvelopeSimple, CaretRight, Eye, Printer, FilePdf, Receipt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { openPdfInBrowser, printPdf } from '../utils/pdfUtils';
import DatePickerDMY from '../components/DatePickerDMY';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '—';
  const p = (d || '').split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

const StatusBadge = ({ status, t }) => {
  const map = {
    paid: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: t('paid') || 'Paid' },
    partially_paid: { cls: 'bg-blue-100 text-blue-700 border-blue-200', label: t('partiallyPaid') || 'Partial' },
    overdue: { cls: 'bg-red-100 text-red-700 border-red-200', label: t('overdue') || 'Overdue' },
    unpaid: { cls: 'bg-zinc-100 text-zinc-700 border-zinc-200', label: t('unpaid') || 'Unpaid' },
  };
  const m = map[status] || map.unpaid;
  return <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${m.cls}`}>{m.label}</span>;
};

export default function BillVendorDetails() {
  const { vendorId } = useParams();
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('invoices');
  const TABS = ['invoices', 'payments', 'statement'];
  const [bills, setBills] = useState([]);
  const [vendor, setVendor] = useState(null);
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [statement, setStatement] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(true);

  // Statement filters
  const [stmtFrom, setStmtFrom] = useState('');
  const [stmtTo, setStmtTo] = useState('');
  const [stmtStatus, setStmtStatus] = useState('all');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bill-vendors/${vendorId}/bills`);
      setVendor(res.data.vendor);
      setBills(res.data.bills || []);
      setSummary(res.data.summary || null);
    } catch (err) { toast.error(t('errorOccurred')); navigate('/bill-vendors'); }
    setLoading(false);
  }, [vendorId, t, navigate]);

  const fetchPayments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bill-vendors/${vendorId}/payments`);
      setPayments(res.data || []);
    } catch (err) { console.error(err); }
  }, [vendorId]);

  const fetchStatement = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (stmtFrom) params.append('date_from', stmtFrom);
      if (stmtTo) params.append('date_to', stmtTo);
      if (stmtStatus && stmtStatus !== 'all') params.append('status', stmtStatus);
      const res = await axios.get(`${API}/bill-vendors/${vendorId}/statement?${params}`);
      setStatement({ rows: res.data.rows || [], summary: res.data.summary || null });
    } catch (err) { console.error(err); }
  }, [vendorId, stmtFrom, stmtTo, stmtStatus]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { if (activeTab === 'payments') fetchPayments(); }, [activeTab, fetchPayments]);
  useEffect(() => { if (activeTab === 'statement') fetchStatement(); }, [activeTab, fetchStatement]);

  if (loading || !vendor) return <Layout><div className="p-8 text-zinc-400 text-sm">{t('loading')}…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-5">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-zinc-500" data-testid="bill-vendor-breadcrumb">
          <button onClick={() => navigate('/bills')} className="hover:text-zinc-900 transition-colors font-medium">{t('bills') || 'Bills'}</button>
          <CaretRight size={12} className="text-zinc-300" />
          <button onClick={() => navigate('/bill-vendors')} className="hover:text-zinc-900 transition-colors font-medium">{t('billVendors') || 'Bills'}</button>
          <CaretRight size={12} className="text-zinc-300" />
          <span className="text-zinc-800 font-semibold truncate max-w-xs">{vendor.name}</span>
        </nav>

        {/* Header */}
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-start gap-3">
            <button onClick={() => navigate('/bill-vendors')} className="p-1 mt-0.5 hover:bg-zinc-100 transition-colors" data-testid="back-to-bill-vendors">
              <ArrowLeft size={18} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold tracking-tight text-zinc-900" data-testid="bill-vendor-name">{vendor.name}</h1>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-600" data-testid="bill-vendor-info">
                {vendor.category && <span><span className="text-zinc-400 uppercase tracking-wider text-[9px] mr-1.5">{t('category') || 'Category'}:</span><span className="font-medium">{vendor.category}</span></span>}
                {vendor.contact_person && <span><span className="text-zinc-400 uppercase tracking-wider text-[9px] mr-1.5">{t('contactPerson') || 'Contact'}:</span><span>{vendor.contact_person}</span></span>}
                {vendor.phone && <span className="inline-flex items-center gap-1"><Phone size={11} className="text-zinc-400" /><span className="font-mono">{vendor.phone}</span></span>}
                {vendor.email && <span className="inline-flex items-center gap-1"><EnvelopeSimple size={11} className="text-zinc-400" /><span>{vendor.email}</span></span>}
                {vendor.account_number && <span><span className="text-zinc-400 uppercase tracking-wider text-[9px] mr-1.5">{t('accountNumber') || 'Acct'}:</span><span className="font-mono">{vendor.account_number}</span></span>}
              </div>
            </div>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => navigate('/bill-vendors')} className="rounded-none flex-shrink-0" data-testid="edit-bill-vendor-btn">
                <PencilSimple size={14} className="mr-1.5" />{t('edit') || 'Edit'}
              </Button>
            )}
          </div>

          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-zinc-200" data-testid="bill-vendor-summary">
              <div className="px-5 py-4">
                <span className="text-[10px] text-zinc-400 uppercase block mb-1">{t('totalInvoiced') || 'Total Invoiced'}</span>
                <span className="text-xl font-bold font-mono">€{summary.total_invoiced.toFixed(2)}</span>
                <p className="text-[10px] text-zinc-400 mt-0.5">{summary.invoice_count} {t('invoices') || 'invoices'}</p>
              </div>
              <div className="px-5 py-4">
                <span className="text-[10px] text-zinc-400 uppercase block mb-1">{t('totalPaid') || 'Total Paid'}</span>
                <span className="text-xl font-bold font-mono text-emerald-700">€{summary.total_paid.toFixed(2)}</span>
              </div>
              <div className="px-5 py-4">
                <span className="text-[10px] text-zinc-400 uppercase block mb-1">{t('totalUnpaid') || 'Total Unpaid'}</span>
                <span className={`text-xl font-bold font-mono ${summary.total_unpaid > 0 ? 'text-red-600' : 'text-emerald-700'}`}>€{summary.total_unpaid.toFixed(2)}</span>
              </div>
              <div className={`px-5 py-4 ${summary.overdue_count > 0 ? 'bg-red-50' : ''}`}>
                <span className="text-[10px] text-zinc-400 uppercase block mb-1">{t('overdueInvoices') || 'Overdue'}</span>
                <span className={`text-xl font-bold font-mono ${summary.overdue_count > 0 ? 'text-red-700' : 'text-zinc-700'}`}>{summary.overdue_count}</span>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="border-t border-zinc-100 flex gap-0 overflow-x-auto" data-testid="bvendor-tabs">
            {TABS.map(tab => {
              const labelKey = tab === 'invoices' ? 'invoices' : tab === 'payments' ? 'payments' : 'statementsTab';
              const fallback = tab === 'invoices' ? 'Invoices' : tab === 'payments' ? 'Payments' : 'Statement';
              return (
                <button key={tab} type="button" onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${activeTab === tab ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'}`}
                  data-testid={`bvendor-tab-${tab}`}
                >
                  {(t(labelKey) === labelKey ? fallback : t(labelKey))}
                </button>
              );
            })}
          </div>
        </div>

        {/* INVOICES TAB */}
        {activeTab === 'invoices' && (
          <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="bvendor-invoices">
            {bills.length === 0 ? (
              <div className="p-12 text-center text-zinc-400 text-sm">{t('noInvoices') || 'No invoices yet.'}</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                    <th className="text-left px-4 py-2.5 w-32">{t('invoiceNumber') || 'Invoice #'}</th>
                    <th className="text-left px-4 py-2.5">{t('period') || 'Period'}</th>
                    <th className="text-left px-4 py-2.5 w-28">{t('issueDate') || 'Issue'}</th>
                    <th className="text-left px-4 py-2.5 w-28">{t('dueDate') || 'Due'}</th>
                    <th className="text-right px-4 py-2.5 w-24">{t('amountExVat') || 'ex VAT'}</th>
                    <th className="text-right px-4 py-2.5 w-16">{t('vatPercent') || 'VAT %'}</th>
                    <th className="text-right px-4 py-2.5 w-24">{t('totalIncVat') || 'Total'}</th>
                    <th className="text-left px-4 py-2.5 w-24">{t('status') || 'Status'}</th>
                    <th className="text-right px-4 py-2.5 w-32">&nbsp;</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {bills.map(b => (
                    <tr key={b.bill_id} className="hover:bg-zinc-50 transition-colors" data-testid={`bvendor-invoice-${b.bill_id}`}>
                      <td className="px-4 py-2 font-mono text-xs">{b.invoice_number || '—'}</td>
                      <td className="px-4 py-2 text-zinc-700">{b.period || '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs text-zinc-500">{formatDateDMY(b.issue_date || b.start_date)}</td>
                      <td className="px-4 py-2 font-mono text-xs text-zinc-500">{formatDateDMY(b.due_date || b.next_payment_date)}</td>
                      <td className="px-4 py-2 text-right font-mono">€{(b.amount_ex_vat ?? b.amount ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">{(b.vat_percent ?? 0).toFixed(0)}%</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold">€{(b.amount || 0).toFixed(2)}</td>
                      <td className="px-4 py-2"><StatusBadge status={b.status} t={t} /></td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <button onClick={() => navigate(`/bills?focus=${b.bill_id}`)} className="p-1 text-zinc-400 hover:text-blue-600 hover:bg-blue-50" title={t('view') || 'View'} data-testid={`view-vbill-${b.bill_id}`}><Eye size={13} /></button>
                          <button onClick={() => openPdfInBrowser(`/bills/${b.bill_id}/pdf`)} className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100" title={t('printPdf') || 'Print PDF'} data-testid={`print-vbill-${b.bill_id}`}><Printer size={13} /></button>
                          <button onClick={() => printPdf(`/bills/${b.bill_id}/pdf`, { download: true })} className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100" title={t('downloadPdf') || 'Download PDF'} data-testid={`download-vbill-${b.bill_id}`}><FilePdf size={13} /></button>
                          {b.attachment_path && (
                            <a href={`${API}/bills/${b.bill_id}/attachment`} target="_blank" rel="noopener noreferrer" className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100" title={t('attachment') || 'Attachment'} data-testid={`attach-vbill-${b.bill_id}`}><Receipt size={13} /></a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* PAYMENTS TAB */}
        {activeTab === 'payments' && (
          <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="bvendor-payments">
            {payments.length === 0 ? (
              <div className="p-12 text-center text-zinc-400 text-sm">{t('noPayments') || 'No payments recorded for this vendor.'}</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                    <th className="text-left px-4 py-2.5 w-28">{t('date') || 'Date'}</th>
                    <th className="text-left px-4 py-2.5 w-32">{t('invoiceNumber') || 'Invoice #'}</th>
                    <th className="text-left px-4 py-2.5">{t('period') || 'Period'}</th>
                    <th className="text-left px-4 py-2.5 w-28">{t('paymentMethod') || 'Method'}</th>
                    <th className="text-right px-4 py-2.5 w-28">{t('amount') || 'Amount'}</th>
                    <th className="text-left px-4 py-2.5">{t('notes') || 'Notes'}</th>
                    <th className="text-right px-4 py-2.5 w-24">&nbsp;</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {payments.map(p => (
                    <tr key={p.payment_id} className="hover:bg-zinc-50 transition-colors" data-testid={`bvendor-payment-${p.payment_id}`}>
                      <td className="px-4 py-2 font-mono text-xs">{formatDateDMY(p.date)}</td>
                      <td className="px-4 py-2 font-mono text-xs">{p.invoice_number || '—'}</td>
                      <td className="px-4 py-2 text-zinc-700">{p.bill_period || '—'}</td>
                      <td className="px-4 py-2 text-zinc-700 text-xs">{p.payment_method || '—'}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold text-emerald-700">€{(p.amount || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-zinc-500 text-xs truncate">{p.notes || '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => navigate(`/bills?focus=${p.bill_id}`)} className="p-1 text-zinc-400 hover:text-blue-600 hover:bg-blue-50" title={t('view') || 'View invoice'} data-testid={`view-vpay-${p.payment_id}`}>
                          <Eye size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* STATEMENT TAB */}
        {activeTab === 'statement' && (
          <div className="space-y-4" data-testid="bvendor-statement">
            {/* Filters */}
            <div className="bg-white border border-zinc-200 p-3 flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs text-zinc-500">{t('fromDate') || 'From'}</Label>
                <DatePickerDMY value={stmtFrom} onChange={setStmtFrom} className="rounded-none h-9 text-sm font-mono mt-1 w-[140px]" data-testid="vstmt-from" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('toDate') || 'To'}</Label>
                <DatePickerDMY value={stmtTo} onChange={setStmtTo} className="rounded-none h-9 text-sm font-mono mt-1 w-[140px]" data-testid="vstmt-to" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('status') || 'Status'}</Label>
                <select value={stmtStatus} onChange={e => setStmtStatus(e.target.value)} className="border border-zinc-200 rounded-none h-9 px-2 text-sm mt-1" data-testid="vstmt-status">
                  <option value="all">{t('all') || 'All'}</option>
                  <option value="paid">{t('paid') || 'Paid'}</option>
                  <option value="unpaid">{t('unpaid') || 'Unpaid'}</option>
                  <option value="partially_paid">{t('partiallyPaid') || 'Partial'}</option>
                  <option value="overdue">{t('overdue') || 'Overdue'}</option>
                </select>
              </div>
              <Button onClick={fetchStatement} className="rounded-none bg-zinc-900 hover:bg-zinc-800 h-9 text-sm" data-testid="vstmt-search">
                {t('search') || 'Search'}
              </Button>
              {(stmtFrom || stmtTo || stmtStatus !== 'all') && (
                <Button variant="outline" onClick={() => { setStmtFrom(''); setStmtTo(''); setStmtStatus('all'); }} className="rounded-none h-9 text-xs" data-testid="vstmt-clear">
                  {t('clearFilter') || 'Clear'}
                </Button>
              )}
            </div>

            {/* Summary */}
            {statement.summary && (
              <div className="grid grid-cols-3 divide-x divide-zinc-200 bg-white border border-zinc-200">
                <div className="px-5 py-3"><span className="text-[10px] text-zinc-400 uppercase block">{t('debits') || 'Total Debits'}</span><span className="text-lg font-bold font-mono">€{statement.summary.debits.toFixed(2)}</span></div>
                <div className="px-5 py-3"><span className="text-[10px] text-zinc-400 uppercase block">{t('credits') || 'Total Credits'}</span><span className="text-lg font-bold font-mono text-emerald-700">€{statement.summary.credits.toFixed(2)}</span></div>
                <div className={`px-5 py-3 ${statement.summary.closing > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}><span className="text-[10px] uppercase block" style={{ color: statement.summary.closing > 0 ? '#b91c1c' : '#047857' }}>{t('closingBalance') || 'Closing Balance'}</span><span className={`text-lg font-bold font-mono ${statement.summary.closing > 0 ? 'text-red-700' : 'text-emerald-700'}`}>€{statement.summary.closing.toFixed(2)}</span></div>
              </div>
            )}

            {/* Ledger table */}
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              {statement.rows.length === 0 ? (
                <div className="p-12 text-center text-zinc-400 text-sm">{t('noData') || 'No transactions in this range.'}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      <th className="text-left px-4 py-2.5 w-28">{t('date') || 'Date'}</th>
                      <th className="text-left px-4 py-2.5">{t('description') || 'Description'}</th>
                      <th className="text-right px-4 py-2.5 w-28">{t('debits') || 'Debit'}</th>
                      <th className="text-right px-4 py-2.5 w-28">{t('credits') || 'Credit'}</th>
                      <th className="text-right px-4 py-2.5 w-28">{t('balance') || 'Balance'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {statement.rows.map((r, i) => {
                      const isInv = r.row_type === 'invoice';
                      return (
                        <tr key={`${r.row_type}-${i}`} className={`${isInv ? '' : 'bg-emerald-50/20'} hover:bg-zinc-50 transition-colors`} data-testid={`vstmt-row-${i}`}>
                          <td className="px-4 py-2 font-mono text-xs text-zinc-500">{formatDateDMY(r.date)}</td>
                          <td className="px-4 py-2 text-zinc-700">{r.description}</td>
                          <td className="px-4 py-2 text-right font-mono">{r.debit > 0 ? <>€{r.debit.toFixed(2)}</> : ''}</td>
                          <td className="px-4 py-2 text-right font-mono text-emerald-700">{r.credit > 0 ? <>€{r.credit.toFixed(2)}</> : ''}</td>
                          <td className={`px-4 py-2 text-right font-mono font-bold ${r.running_balance > 0 ? 'text-red-600' : r.running_balance < 0 ? 'text-emerald-700' : 'text-zinc-700'}`}>€{r.running_balance.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
